import {
  BaseInputType,
  binaryResultCopyTapped,
  convertTapped,
  decimalResultCopyTapped,
  fromTypeChanged,
  hexResultCopyTapped,
  inputChanged,
  octalResultCopyTapped,
  selectState,
} from "./BaseConverterSlice";

import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { EqualColumnTable } from "../../components/equal_column_table/EqualColumnTable";
import { ResultCard } from "../../components/result_card/ResultCard";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { useTranslation } from "react-i18next";
import { HomeCard } from "../../components/home_card/HomeCard";
import DistanceUnitConverterImage from "../../assets/DistanceUnitConverter.webp";
import WeightUnitConverterImage from "../../assets/WeightUnitConverter.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function BaseConverter() {
  const baseConverterState = useAppSelector(selectState);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  var helpText = "";

  switch (baseConverterState.inputType) {
    case BaseInputType.binary:
      helpText = "10101";
      break;
    case BaseInputType.octal:
      helpText = "10";
      break;
    case BaseInputType.decimal:
      helpText = "15";
      break;
    case BaseInputType.hex:
      helpText = "FF";
      break;
  }
  return (
    <SideMenuPageContainer
      pageTitle={t("baseConverter.title")}
      pageSeoTitle={t("baseConverter.seoTitle")}
      pageDescription={t("baseConverter.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("baseConverter.mainHeading")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <CustomSelect
            label={t("baseConverter.fromLabel")}
            value={baseConverterState.inputType}
            onChange={(e) =>
              dispatch(fromTypeChanged(Number(e.target.value) as BaseInputType))
            }
          >
            <MenuItem value={BaseInputType.binary}>{t("baseConverter.options.binary")}</MenuItem>
            <MenuItem value={BaseInputType.octal}>{t("baseConverter.options.octal")}</MenuItem>
            <MenuItem value={BaseInputType.decimal}>{t("baseConverter.options.decimal")}</MenuItem>
            <MenuItem value={BaseInputType.hex}>{t("baseConverter.options.hex")}</MenuItem>
          </CustomSelect>
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("baseConverter.valueLabel")}
            variant="outlined"
            color="info"
            helperText={
              <>
                <Typography variant="helptext" sx={{ mr: 1 }}>
                  {t("baseConverter.examplePrefix")}
                </Typography>

                <Typography variant="helptext">{helpText}</Typography>
              </>
            }
            value={baseConverterState.input || ""}
            onChange={(e) => {
              dispatch(inputChanged(e.target.value));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>{baseConverterState.valuePrefix}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 1, mb: 3 }}>
          <CustomButton
            label={t("baseConverter.convertButton")}
            onClick={() => {
              dispatch(convertTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`0b${baseConverterState.outputBinary}`}
            symbol={``}
            description={t("baseConverter.result.binary")}
            onCopy={() => dispatch(binaryResultCopyTapped())}
            resultCopied={baseConverterState.binaryResultCopied}
          />
          <ResultCard
            value={`${baseConverterState.outputOctal}`}
            symbol={``}
            description={t("baseConverter.result.octal")}
            onCopy={() => dispatch(octalResultCopyTapped())}
            resultCopied={baseConverterState.octalResultCopied}
          />
          <ResultCard
            value={`${baseConverterState.outputDecimal}`}
            symbol={``}
            description={t("baseConverter.result.decimal")}
            onCopy={() => dispatch(decimalResultCopyTapped())}
            resultCopied={baseConverterState.decimalResultCopied}
          />
          <ResultCard
            value={`0x${baseConverterState.outputHex}`}
            symbol={``}
            description={t("baseConverter.result.hex")}
            onCopy={() => dispatch(hexResultCopyTapped())}
            resultCopied={baseConverterState.hexResultCopied}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>
        <Grid2 xs={12}>
          <AdSense
              adSlot={ADSENSE_SLOTS.IN_ARTICLE}
              adFormat="fluid"
              adLayoutKey="-fb+5w+4e-db+86"
              sx={{ mb: 3 }}
            />
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("baseConverter.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("baseConverter.content.freeDescription")}
          </Typography>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("baseConverter.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("baseConverter.content.howToDescription")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("baseConverter.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="distanceConverter"
            link="/DistanceUnitConverter"
            smallCard={true}
            image={DistanceUnitConverterImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="weightConverter"
            link="/WeightUnitConverter"
            smallCard={true}
            image={WeightUnitConverterImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
